import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { LookupAutocomplete, type LookupOption } from '../ui/lookup-autocomplete';
import { toast } from 'sonner';

import { useOrderActions } from '../../services/orders';
import { useShippingLocationsApi } from '../../services/shippingLocations';
import { useSuppliersApi } from '../../services/suppliers';
import { useSubprojectsApi } from '../../services/subprojects';
import { useAppSettings } from '../../hook/useAppSettings';
import type { Room, RoomItem } from '../../types/subprojects';
import type { DecimalString } from '../../types/subprojects';

interface SubprojectOrdersDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  subprojectId: string;
  rooms: Room[];
  onCreated?: () => void;
}

type SupplierLine = { roomId: string; item: RoomItem };

type SupplierGroup = { supplierId: string; supplierName: string; lines: SupplierLine[] };

type SupplierFormDraft = {
  orderNumber: string;
  shippingLocationName: string;
  shippingLocationId: string;
  expectedDeliveryDate: string;
};

type SupplierFormsBySupplier = Record<string, SupplierFormDraft>;

type SupplierOrderDraft = {
  supplierId: string;
  supplierName: string;
  selectedLines: SupplierLine[];
  missingLines: SupplierLine[];
  draft: SupplierFormDraft;
};

type OrderGenerationSummary = {
  supplierDrafts: SupplierOrderDraft[];
  ordersToCreate: number;
  selectedItemsCount: number;
  missingItemsCount: number;
  supplierNamesWithMissingItems: string[];
};

const EMPTY_SUPPLIER_FORM_DRAFT: SupplierFormDraft = {
  orderNumber: '',
  shippingLocationName: '',
  shippingLocationId: '',
  expectedDeliveryDate: '',
};

const buildSupplierGroups = (rooms: Room[], supplierNames: Record<string, string>): SupplierGroup[] => {
  const groups: Record<string, SupplierGroup> = {};

  rooms.forEach((room) => {
    room.items
      .filter((item) => item.state === 'pendiente')
      .forEach((item) => {
        const rawId = item.supplier_id ?? item.supplier;
        const supplierId = rawId && rawId.trim() ? rawId : 'sin-proveedor';
        const supplierName = supplierNames[supplierId] ?? item.supplier ?? supplierId;

        if (!groups[supplierId]) {
          groups[supplierId] = { supplierId, supplierName, lines: [] };
        }
        groups[supplierId].lines.push({ roomId: room.id, item });
      });
  });

  return Object.values(groups);
};

const buildOrderGenerationSummary = (
  groups: SupplierGroup[],
  selectedSuppliers: Record<string, boolean>,
  lineSelection: Record<string, boolean>,
  formBySupplier: SupplierFormsBySupplier,
): OrderGenerationSummary => {
  const supplierDrafts = groups
    .filter((group) => selectedSuppliers[group.supplierId] ?? false)
    .map((group) => {
      const selectedLines = group.lines.filter(({ item }) => lineSelection[item.id] ?? true);
      const missingLines = group.lines.filter(({ item }) => !(lineSelection[item.id] ?? true));

      return {
        supplierId: group.supplierId,
        supplierName: group.supplierName,
        selectedLines,
        missingLines,
        draft: formBySupplier[group.supplierId] ?? EMPTY_SUPPLIER_FORM_DRAFT,
      };
    });

  const ordersToCreate = supplierDrafts.filter((draft) => draft.selectedLines.length > 0).length;
  const selectedItemsCount = supplierDrafts.reduce(
    (total, draft) => total + draft.selectedLines.length,
    0,
  );
  const missingItemsCount = supplierDrafts.reduce(
    (total, draft) => total + draft.missingLines.length,
    0,
  );
  const supplierNamesWithMissingItems = supplierDrafts
    .filter((draft) => draft.selectedLines.length > 0 && draft.missingLines.length > 0)
    .map((draft) => draft.supplierName);

  return {
    supplierDrafts,
    ordersToCreate,
    selectedItemsCount,
    missingItemsCount,
    supplierNamesWithMissingItems,
  };
};

const validateOrderGenerationSummary = (summary: OrderGenerationSummary): string | null => {
  if (!summary.supplierDrafts.length) {
    return 'Selecciona al menos un proveedor con ítems.';
  }

  for (const draft of summary.supplierDrafts) {
    if (!draft.selectedLines.length) {
      return `Has seleccionado ${draft.supplierName} pero no tiene ítems marcados. Marca al menos un ítem o desmarca el proveedor.`;
    }

    const orderNumber = draft.draft.orderNumber.trim();
    if (!orderNumber) {
      return `Añade Nº pedido para ${draft.supplierName}`;
    }

    if (draft.draft.shippingLocationName.trim() && !draft.draft.shippingLocationId) {
      return `Selecciona un lugar de envío válido para ${draft.supplierName}`;
    }
  }

  if (summary.ordersToCreate === 0) {
    return 'Selecciona al menos un proveedor con ítems.';
  }

  return null;
};

export function SubprojectOrdersDialog({
  open, onClose, projectId, subprojectId, rooms, onCreated,
}: SubprojectOrdersDialogProps) {
  const { formatCurrency } = useAppSettings();
  const { createOrder } = useOrderActions();
  const { updateRoomItem } = useSubprojectsApi();
  const { data: suppliers } = useSuppliersApi({ search: undefined, active: undefined }, { page: 1, page_size: 100 });
  const {
    shippingLocations,
    loading: shippingLocationsLoading,
    refresh: refreshShippingLocations,
  } = useShippingLocationsApi({ active: true, limit: 200 });

  const supplierNames = useMemo(
    () => suppliers.reduce<Record<string, string>>((acc, s) => ({ ...acc, [s.id]: s.name }), {}),
    [suppliers],
  );

  const groups = useMemo(() => buildSupplierGroups(rooms, supplierNames), [rooms, supplierNames]);
  const shippingLocationOptions = useMemo<LookupOption[]>(
    () =>
      shippingLocations.map((shippingLocation) => ({
        label: shippingLocation.name,
        value: shippingLocation.id,
      })),
    [shippingLocations],
  );
  const [selectedSuppliers, setSelectedSuppliers] = useState<Record<string, boolean>>({});
  const [lineSelection, setLineSelection] = useState<Record<string, boolean>>({});
  const [formBySupplier, setFormBySupplier] = useState<SupplierFormsBySupplier>({});
  const [submitting, setSubmitting] = useState(false);
  const [partialSelectionWarningOpen, setPartialSelectionWarningOpen] = useState(false);
  const [pendingGenerationSummary, setPendingGenerationSummary] = useState<OrderGenerationSummary | null>(null);

  const buildItemPayload = (item: RoomItem) => ({
    ref: item.ref,
    supplier_id: item.supplier_id,
    supplier: item.supplier,
    description: item.description,
    category_id: item.category_id,
    quantity: item.quantity,
    precio_tarifa: item.precio_tarifa,
    desvio_comercial_pct: item.desvio_comercial_pct,
    beneficio_comercial_pct: item.beneficio_comercial_pct,
    montaje_pct: item.montaje_pct,
    discount: item.discount,
    desvio_personalizado: item.desvio_personalizado,
    beneficio_personalizado: item.beneficio_personalizado,
    real_cost: item.real_cost,
    real_cost_modi: item.real_cost_modi,
    notes: item.notes,
    state: 'pedido' as const,
  });

  const clearPendingConfirmation = () => {
    setPartialSelectionWarningOpen(false);
    setPendingGenerationSummary(null);
  };

  const handleToggleSupplier = (supplierId: string, checked: boolean, lines: SupplierGroup['lines']) => {
    setSelectedSuppliers((prev) => ({ ...prev, [supplierId]: checked }));
    setLineSelection((prev) => {
      const next = { ...prev };
      lines.forEach(({ item }) => {
        next[item.id] = checked ? (prev[item.id] ?? true) : false;
      });
      return next;
    });
  };

  const executeOrderGeneration = async (summary: OrderGenerationSummary) => {
    setSubmitting(true);
    try {
      for (const draft of summary.supplierDrafts) {
        if (!draft.selectedLines.length) continue;
        const orderNumber = draft.draft.orderNumber.trim();

        try {
          await createOrder({
            project_id: projectId,
            subproject_id: subprojectId,
            supplier_id: draft.supplierId,
            order_number: orderNumber,
            shipping_location_id: draft.draft.shippingLocationId || undefined,
            expected_delivery_date: draft.draft.expectedDeliveryDate || undefined,
            items: draft.selectedLines.map(({ item }) => ({
              room_item_id: item.id,
              description: item.description,
              quantity: item.quantity,
              unit_cost: item.unit_cost,
              line_total_cost: (Number(item.unit_cost) * item.quantity).toFixed(2) as DecimalString,
            })),
          });
        } catch {
          toast.error(`No se pudo crear el pedido para ${draft.supplierName}. Usa un Nº de pedido único y vuelve a intentarlo.`);
          return;
        }

        await Promise.all(
          draft.selectedLines.map(({ roomId, item }) =>
            updateRoomItem(subprojectId, roomId, item.id, buildItemPayload(item)),
          ),
        );
      }
      toast.success('Pedidos generados');
      onCreated?.();
      clearPendingConfirmation();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No pudimos crear los pedidos');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateOrdersClick = () => {
    const summary = buildOrderGenerationSummary(groups, selectedSuppliers, lineSelection, formBySupplier);
    const validationError = validateOrderGenerationSummary(summary);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (summary.missingItemsCount > 0) {
      setPendingGenerationSummary(summary);
      setPartialSelectionWarningOpen(true);
      return;
    }

    void executeOrderGeneration(summary);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    clearPendingConfirmation();
    onClose();
  };


  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!next) handleCloseDialog(); }}>
        <DialogContent className="w-[95vw] max-w-6xl sm:min-w-[1000px] max-h-[85vh] p-0 overflow-hidden flex flex-col">
          <div className="sticky top-0 z-10 border-b bg-white/95 px-6 pt-6 pb-4 backdrop-blur">
            <DialogHeader className="p-0">
              <DialogTitle>Generar pedidos</DialogTitle>
              <DialogDescription>Selecciona proveedores e items pendientes para crear los pedidos.</DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {groups.map((group) => {
              const selectedLines = group.lines.filter(({ item }) => lineSelection[item.id] ?? true);
              const totalQuantity = selectedLines.reduce((acc, { item }) => acc + (item.quantity ?? 0), 0);
              const totalCost = selectedLines.reduce(
                (acc, { item }) => acc + Number(item.unit_cost) * (item.quantity ?? 0),
                0,
              );

              return (
                <Card key={group.supplierId} className="border shadow-sm">
                  <CardHeader className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedSuppliers[group.supplierId] ?? false}
                        onCheckedChange={(checked) => handleToggleSupplier(group.supplierId, Boolean(checked), group.lines)}
                      />
                      <CardTitle className="text-base">{group.supplierName}</CardTitle>
                      <Badge variant="outline">{group.lines.length} pendientes</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Input
                        placeholder="Nº pedido"
                        value={formBySupplier[group.supplierId]?.orderNumber ?? ''}
                        onChange={(e) => setFormBySupplier((prev) => ({
                          ...prev,
                          [group.supplierId]: { ...(prev[group.supplierId] ?? EMPTY_SUPPLIER_FORM_DRAFT), orderNumber: e.target.value },
                        }))}
                        className="w-40"
                        disabled={submitting}
                      />
                      <div className="w-60">
                        <LookupAutocomplete
                          placeholder="Lugar de envío"
                          value={formBySupplier[group.supplierId]?.shippingLocationName ?? ''}
                          options={shippingLocationOptions}
                          loading={shippingLocationsLoading}
                          disabled={submitting}
                          onSearch={(query) => {
                            void refreshShippingLocations({
                              search: query.trim() || undefined,
                              active: true,
                              limit: 200,
                            });
                          }}
                          onTextChange={(next) =>
                            setFormBySupplier((prev) => ({
                              ...prev,
                              [group.supplierId]: {
                                ...(prev[group.supplierId] ?? EMPTY_SUPPLIER_FORM_DRAFT),
                                shippingLocationName: next,
                                shippingLocationId: '',
                              },
                            }))
                          }
                          onSelect={({ label, value }) =>
                            setFormBySupplier((prev) => ({
                              ...prev,
                              [group.supplierId]: {
                                ...(prev[group.supplierId] ?? EMPTY_SUPPLIER_FORM_DRAFT),
                                shippingLocationName: label,
                                shippingLocationId: value,
                              },
                            }))
                          }
                        />
                      </div>
                      <Input
                        type="date"
                        value={formBySupplier[group.supplierId]?.expectedDeliveryDate ?? ''}
                        onChange={(e) => setFormBySupplier((prev) => ({
                          ...prev,
                          [group.supplierId]: { ...(prev[group.supplierId] ?? EMPTY_SUPPLIER_FORM_DRAFT), expectedDeliveryDate: e.target.value },
                        }))}
                        className="w-44"
                        disabled={submitting}
                      />
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Descripción</TableHead>
                          <TableHead className="text-center">Cantidad</TableHead>
                          <TableHead className="text-center">Coste</TableHead>
                          <TableHead className="text-center">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.lines.map(({ item }) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Checkbox
                                checked={
                                  (selectedSuppliers[group.supplierId] ?? false)
                                    ? lineSelection[item.id] ?? true
                                    : false
                                }
                                onCheckedChange={(checked) =>
                                  setLineSelection((prev) => ({ ...prev, [item.id]: Boolean(checked) }))
                                }
                                disabled={submitting || !(selectedSuppliers[group.supplierId] ?? false)}
                              />
                            </TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-center">
                              {formatCurrency(Number(item.unit_cost) * (item.quantity ?? 0), { showZero: true })}
                            </TableCell>
                            <TableCell className="text-center"><Badge variant="secondary">Pendiente</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="flex justify-end gap-6 text-sm text-gray-800">
                      <span className="font-semibold">Total items: {totalQuantity}</span>
                      <span className="font-semibold">
                        Coste total: {formatCurrency(totalCost, { showZero: true })}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <DialogFooter className="sticky bottom-0 z-10 border-t bg-white/95 px-6 py-4 backdrop-blur">
            <Button variant="outline" onClick={handleCloseDialog} disabled={submitting}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleGenerateOrdersClick} disabled={submitting}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Generar pedidos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={partialSelectionWarningOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !submitting) {
            clearPendingConfirmation();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Generar pedidos dejando ítems pendientes?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-gray-700">
              <div>
                Se crearán {pendingGenerationSummary?.ordersToCreate ?? 0} pedido(s) con{' '}
                {pendingGenerationSummary?.selectedItemsCount ?? 0} ítem(s) seleccionados y quedarán{' '}
                {pendingGenerationSummary?.missingItemsCount ?? 0} ítem(s) sin pedir en proveedores seleccionados.
              </div>
              <div>
                El aviso aplica a los siguientes proveedores: {pendingGenerationSummary?.supplierNamesWithMissingItems.join(', ') || '—'}.
              </div>
              <div>
                Podrás generar esos ítems pendientes más tarde desde este mismo diálogo.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={submitting}
              onClick={(event) => {
                event.preventDefault();
                clearPendingConfirmation();
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-blue-600 hover:bg-blue-700"
              disabled={submitting}
              onClick={(event) => {
                event.preventDefault();
                const summary = pendingGenerationSummary;
                if (!summary) {
                  clearPendingConfirmation();
                  return;
                }
                setPartialSelectionWarningOpen(false);
                void executeOrderGeneration(summary);
              }}
            >
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Generar de todos modos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
